/**
 * RecordModal — one row's full record, over the list it came from.
 *
 * ── WHY A MODAL AND NOT THE ROUTED PAGE ──
 *
 * A list of hits is a POSITION an operator is working through: a query, a filter, a page, a
 * scroll offset. Navigating away to read one of them throws all of it away, and coming back
 * re-runs the query and lands somewhere else — so reading the third of twenty hits costs the
 * other seventeen. The record opens over the list instead, and ← / → step through the hits
 * without closing it.
 *
 * The routed page is not replaced: it stays reachable from the header, because a record
 * sometimes has to outlive the search that found it (a link in a ticket, a bookmark).
 *
 * ── WHY IT OWNS THE FETCH ──
 *
 * Because the pager is what makes the modal worth having, and paging means re-fetching. A
 * caller holding the loaded record would have to re-implement "load when the index moves, drop
 * the answer if the index moved again while it was in flight" — the race that shows the previous
 * hit's record under this hit's title. One implementation of that, here.
 *
 * ── WHY THE RECORD DRAWS THE HEADER ──
 *
 * The same reason the alert record does: the header says what the LOADED record is (the model,
 * what it called the event, the camera and the time), which the row that opened it only half
 * knows. So the modal has no header of its own; `render` is handed the pager, "Full page" and
 * the close (`RecordChrome`) to put in the record's, and while loading — or when the record is
 * gone — the modal draws a stand-in header so the pager and close still work.
 *
 * `identity` is what keys the fetch. Callers pass the pair the record is looked up by — usually
 * `${model}:${event_id}` — so stepping between two rows OF THE SAME EVENT (a search result is
 * one person of a frame that may hold seven) does NOT refetch what it already has.
 */
import { useEffect, useState, type ReactNode } from 'react';
import Modal from '../common/Modal';
import RecordPager from '../common/RecordPager';
import { RecordFullPageButton } from '../alerts/AlertRecord';
import { EventRecordHeader } from './EventDetail';

/** What the record puts in its own header — see "WHY THE RECORD DRAWS THE HEADER". */
export interface RecordChrome {
  /** The pager, any `headerExtra` and "Full page". */
  headerActions: ReactNode;
  onClose: () => void;
}

export interface RecordModalProps<Row> {
  /** The list being paged through. The modal shows `rows[index]`. */
  rows: Row[];
  /** null closes it. */
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;

  /** Fetch the record for a row. Re-run whenever `identity` changes. */
  load: (row: Row) => Promise<any>;
  /** What keys the fetch — two rows with the same identity share one record. */
  identity: (row: Row) => string;

  /** The header's title when the record could not be loaded — the row alone always knows this. */
  fallbackTitle: (row: Row) => ReactNode;
  /** The record's body, header included — handed the pager / "Full page" / close to put in it. */
  render: (event: any, row: Row, chrome: RecordChrome) => ReactNode;

  /** "results" / "sightings" — what the pager counts, in the reader's words. */
  noun: string;
  /** The routed page for this record, when the host has one. */
  onOpenFull?: (row: Row) => void;
  /** Anything else for the header bar, to the right of the pager. */
  headerExtra?: (row: Row) => ReactNode;
  /**
   * Accessible name. Needed because `title` is a ReactNode a caller builds — a screen reader
   * cannot take a name from a chip and an icon.
   */
  ariaLabel?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

export default function RecordModal<Row>({
  rows,
  index,
  onIndexChange,
  onClose,
  load,
  identity,
  fallbackTitle,
  render,
  noun,
  onOpenFull,
  headerExtra,
  ariaLabel = 'Event details',
  size = 'full',
}: RecordModalProps<Row>) {
  const row = index !== null ? (rows[index] ?? null) : null;
  const key = row ? identity(row) : null;

  const [event, setEvent] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!row) {
      setEvent(null);
      setError(null);
      return undefined;
    }
    // `live` is the race guard the header note describes: the reader can press → twice before
    // the first record arrives, and without this the first answer lands last and wins.
    let live = true;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const ev = await load(row);
        if (!live) return;
        setEvent(ev || null);
        // A row the list has but the source table does not: past retention, or deleted. Said
        // plainly, because an empty modal reads as a broken page rather than a gone record.
        if (!ev) {
          setError('That event is no longer available — it may have been deleted or passed its retention window.');
        }
      } catch {
        if (live) {
          setEvent(null);
          setError('Could not load that event.');
        }
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
    // Keyed on the IDENTITY, not the row object — see the header. A list that re-fetches under
    // the modal hands over new row objects for the same records, and depending on those would
    // reload the open record every time the list refreshed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  if (!row) return null;

  const headerActions = (
    <>
      {/* Steps through the hits on screen without closing — ← / → are bound by the pager
          itself. This is the whole reason the modal beats a page for a list of hits. */}
      <RecordPager
        variant="boxed"
        index={index ?? 0}
        count={rows.length}
        onIndexChange={onIndexChange}
        noun={noun}
      />
      {headerExtra?.(row)}
      {onOpenFull && <RecordFullPageButton what="event" onClick={() => onOpenFull(row)} />}
    </>
  );

  return (
    <Modal open onClose={onClose} size={size} ariaLabel={ariaLabel}>
      {event && !loading ? (
        render(event, row, { headerActions, onClose })
      ) : (
        <div className="flex min-h-full flex-col bg-card">
          <EventRecordHeader
            event={null}
            actions={headerActions}
            onClose={onClose}
            fallbackTitle={error ? fallbackTitle(row) : undefined}
          />
          {error ? (
            <div className="py-16 text-center text-sm text-muted-foreground">{error}</div>
          ) : (
            <div className="grid gap-6 px-6 py-5 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,1fr)]">
              <div className="h-[42vh] animate-pulse rounded-xl bg-muted" />
              <div className="space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-muted" />)}</div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
